import type { LedgerTransactionSortField } from '@/gql/graphql';
import { decimal, int, isoDate, oneOf, optionsOf, text } from '@/hooks/useUrlState';

export type LedgerMethodValue = 'pin' | 'cash' | 'tab';
export const LEDGER_METHOD_LABELS: Record<LedgerMethodValue, string> = { pin: 'PIN', cash: 'Cash', tab: 'Tab' };

export type LedgerPspValue = 'adyen' | 'viva';
export const LEDGER_PSP_LABELS: Record<LedgerPspValue, string> = { adyen: 'Adyen', viva: 'Viva' };

export const LEDGER_METHOD_OPTIONS = optionsOf(LEDGER_METHOD_LABELS, 'Any method');
export const LEDGER_PSP_OPTIONS = optionsOf(LEDGER_PSP_LABELS, 'Any PSP');

export type LedgerSortColumn =
  | 'transactionId'
  | 'amount'
  | 'tip'
  | 'method'
  | 'recognitionDate'
  | 'createdAt'
  | 'device'
  | 'category';

/** DataGrid column `id` → GraphQL sort field for server-side sort; its keys are the values `sort` accepts. */
export const LEDGER_COLUMN_SORT: Record<LedgerSortColumn, LedgerTransactionSortField> = {
  transactionId: 'TRANSACTION_ID',
  amount: 'AMOUNT',
  tip: 'AMOUNT_TIP',
  method: 'PAYMENT_METHOD',
  recognitionDate: 'RECOGNITION_DATE',
  createdAt: 'CREATED_AT',
  device: 'DEVICE_NAME',
  category: 'CATEGORY_NAME',
};

export const LEDGER_DEFAULT_SORT: { sort: LedgerSortColumn; dir: 'asc' | 'desc' } = { sort: 'createdAt', dir: 'desc' };

/** The /ledger query string, in this order (see README "List pages, URL state"). */
export const ledgerUrlSchema = {
  search: text(),
  method: oneOf(LEDGER_METHOD_LABELS, ''),
  psp: oneOf(LEDGER_PSP_LABELS, ''),
  amountMin: decimal(),
  amountMax: decimal(),
  from: isoDate(),
  to: isoDate(),
  sort: oneOf(LEDGER_COLUMN_SORT, LEDGER_DEFAULT_SORT.sort),
  dir: oneOf<'asc' | 'desc', 'asc' | 'desc'>({ asc: true, desc: true }, LEDGER_DEFAULT_SORT.dir),
  page: int({ min: 1 }, 1),
  size: int({ min: 1, max: 500 }, 50),
};

/** The keys that live in the collapsible Filters panel: the panel opens on mount when any is set. */
export const LEDGER_ADVANCED_KEYS = [
  'method',
  'psp',
  'amountMin',
  'amountMax',
  'from',
  'to',
] as const satisfies readonly (keyof typeof ledgerUrlSchema)[];
